import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/+esm';
import { GLTFLoader } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/loaders/GLTFLoader.js/+esm';

const instances = new Map();

function hexToRgb(hex, fallback) {
    const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
    const h = m ? m[1] : fallback;
    return [parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255];
}

function splitTitles(root) {
    root.querySelectorAll('.h3d-title').forEach((title) => {
        if (title.dataset.split) return;
        title.dataset.split = '1';
        let n = 0;
        Array.from(title.childNodes).forEach((node) => {
            if (node.nodeType !== 3) return;
            const frag = document.createDocumentFragment();
            Array.from(node.textContent).forEach((ch) => {
                if (ch === ' ') { frag.appendChild(document.createTextNode(' ')); return; }
                if (ch === '\n') return;
                const s = document.createElement('span');
                s.className = 'h3d-char';
                s.style.transitionDelay = (n * 0.035) + 's';
                s.textContent = ch;
                frag.appendChild(s);
                n++;
            });
            title.replaceChild(frag, node);
        });
    });
}

function stickyHeaderHeight() {
    let el = document.querySelector('.header') || document.querySelector('header');
    const target = el;
    while (el && el !== document.body) {
        const pos = getComputedStyle(el).position;
        if (pos === 'sticky' || pos === 'fixed') return Math.round(target.getBoundingClientRect().height);
        el = el.parentElement;
    }
    return 0;
}

const VERT = `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;
const FRAG = `
    varying vec2 vUv;
    uniform float uTime;
    uniform vec2 uResolution;
    uniform vec2 uMouse;
    uniform float uScroll;
    uniform vec3 uAccent;
    void main() {
        vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution.xy) / uResolution.y;
        float aspect = uResolution.x / uResolution.y;
        float time = uTime * 0.08;
        float scroll = uScroll;
        vec2 warpedUv = uv;
        float scrollDeform = scroll * 5.0;
        warpedUv.x += sin(uv.y * 2.5 + time * 0.2 + scrollDeform) * 0.35;
        warpedUv.y += cos(uv.x * 2.5 - time * 0.15 - scrollDeform * 0.8) * 0.35;
        warpedUv.x += sin(uv.y * 1.2 - time * 0.1 - scrollDeform * 1.5) * 0.25;
        warpedUv.y += cos(uv.x * 1.2 + time * 0.18 + scrollDeform * 1.2) * 0.25;
        warpedUv += vec2(scroll * 0.04, -scroll * 0.02) + vec2(uMouse.x * aspect * 0.05, uMouse.y * 0.05);
        vec2 dir1 = vec2(cos(0.6), sin(0.6));
        vec2 dir2 = vec2(cos(-0.7), sin(-0.7));
        vec2 dir3 = vec2(cos(1.2), sin(1.2));
        float w1 = sin(dot(warpedUv, dir1) * 2.4 + time * 1.0);
        float w2 = cos(dot(warpedUv, dir2) * 3.2 - time * 1.4 + w1 * 0.4);
        float w3 = sin(dot(warpedUv, dir3) * 4.0 + time * 1.8 + w2 * 0.5);
        float waveField = w1 * 0.50 + w2 * 0.35 + w3 * 0.15;
        float wideSheen = pow(max(0.0, 1.0 - abs(waveField - 0.1)), 2.5);
        float crispSpecular = pow(max(0.0, 1.0 - abs(waveField - 0.15)), 8.0);
        float crest = wideSheen * 0.5 + crispSpecular * 0.9;
        vec3 c0_shadow = vec3(0.0006, 0.0006, 0.0007);
        vec3 c0_wave1  = vec3(0.030, 0.032, 0.035);
        vec3 c0_wave2  = vec3(0.016, 0.017, 0.019);
        vec3 c0_crest  = vec3(0.22, 0.23, 0.25);
        vec3 c1_shadow = uAccent * 0.0014;
        vec3 c1_wave1  = uAccent * 0.075;
        vec3 c1_wave2  = uAccent * 0.045;
        vec3 c1_crest  = uAccent * 0.68;
        float t = smoothstep(0.0, 1.0, scroll);
        vec3 color = mix(c0_shadow, c1_shadow, t);
        color = mix(color, mix(c0_wave2, c1_wave2, t), smoothstep(-0.6, 0.2, waveField));
        color = mix(color, mix(c0_wave1, c1_wave1, t), smoothstep(0.0, 0.8, waveField));
        color += mix(c0_crest, c1_crest, t) * crest * 1.4;
        color *= 1.0 - dot(uv, uv) * 0.12;
        gl_FragColor = vec4(color, 1.0);
    }
`;

function sparkTexture() {
    const c = document.createElement('canvas');
    c.width = 16; c.height = 16;
    const ctx = c.getContext('2d');
    const g = ctx.createRadialGradient(8, 8, 0, 8, 8, 8);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.25, 'rgba(255,255,255,0.85)');
    g.addColorStop(0.6, 'rgba(255,255,255,0.3)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 16, 16);
    return new THREE.CanvasTexture(c);
}

function init(root) {
    if (!root || instances.has(root)) return;
    let cfg = {};
    try { cfg = JSON.parse(root.querySelector('[data-h3d-config]').textContent); } catch (e) {}
    const stage = root.querySelector('.h3d-stage');
    const canvas = root.querySelector('.h3d-canvas');
    const slides = Array.from(root.querySelectorAll('.h3d-slide'));
    const fills = Array.from(root.querySelectorAll('.h3d-dash-fill'));
    const dots = Array.from(root.querySelectorAll('.h3d-dot'));
    const N = slides.length;
    const state = { raf: 0, dead: false };
    instances.set(root, state);

    splitTitles(root);
    const setTop = () => root.style.setProperty('--h3d-top', stickyHeaderHeight() + 'px');
    setTop();

    const goStatic = () => {
        root.classList.add('h3d--static');
        if (slides[0]) slides[0].classList.add('is-active');
    };
    const small = window.matchMedia('(max-width: 820px)').matches;
    if (small && !cfg.mobile3d) { goStatic(); return; }

    let renderer;
    try {
        renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: 'high-performance' });
    } catch (e) { goStatic(); return; }
    root.classList.add('h3d--live');

    const sizes = { width: stage.clientWidth || window.innerWidth, height: stage.clientHeight || window.innerHeight };
    const accent = hexToRgb(cfg.accent, '2F9DB8');
    const uniforms = {
        uTime: { value: 0 },
        uResolution: { value: new THREE.Vector2(1, 1) },
        uMouse: { value: new THREE.Vector2(0, 0) },
        uScroll: { value: 0 },
        uAccent: { value: new THREE.Vector3(accent[0], accent[1], accent[2]) }
    };

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#0a0a0a');
    scene.fog = new THREE.FogExp2('#0a0a0a', 0.01);
    const camera = new THREE.PerspectiveCamera(50, sizes.width / sizes.height, 0.1, 100);
    camera.position.set(0, 0.2, 3.0);
    scene.add(camera);

    const bg = new THREE.Mesh(
        new THREE.PlaneGeometry(30, 30),
        new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, uniforms, depthWrite: false, depthTest: false })
    );
    bg.position.set(0, 0, -8);
    bg.renderOrder = -10;
    camera.add(bg);

    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 2.2;

    scene.add(new THREE.AmbientLight('#ffffff', 0.1));
    const key = new THREE.SpotLight('#ffffff', 18.0);
    key.position.set(4, 6, 3);
    key.angle = Math.PI / 4;
    key.penumbra = 0.9;
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 1.0;
    key.shadow.camera.far = 15;
    key.shadow.bias = -0.001;
    scene.add(key);
    const rim = new THREE.DirectionalLight('#e3f2ff', 10.0);
    rim.position.set(-5, 3, -4);
    scene.add(rim);
    const fill = new THREE.DirectionalLight('#F2F0EB', 0.8);
    fill.position.set(-2, -4, 2);
    scene.add(fill);

    // sparks
    const sparkCount = cfg.sparks === false ? 0 : 450;
    const sparkData = [];
    let sparks = null;
    if (sparkCount) {
        const positions = new Float32Array(sparkCount * 3);
        const colors = new Float32Array(sparkCount * 3);
        for (let i = 0; i < sparkCount; i++) {
            positions[i * 3] = (Math.random() - 0.5) * 6.5;
            positions[i * 3 + 1] = (Math.random() - 0.5) * 5.0 - 0.5;
            positions[i * 3 + 2] = (Math.random() - 0.5) * 6.5;
            if (Math.random() < 0.6) {
                const g = 0.85 + Math.random() * 0.15;
                colors[i * 3] = g; colors[i * 3 + 1] = g; colors[i * 3 + 2] = g * 0.97;
            } else {
                const k = 1.15 + Math.random() * 0.2;
                colors[i * 3] = Math.min(1, accent[0] * k + 0.05);
                colors[i * 3 + 1] = Math.min(1, accent[1] * k + 0.05);
                colors[i * 3 + 2] = Math.min(1, accent[2] * k + 0.05);
            }
            sparkData.push({
                speedX: (Math.random() - 0.5) * 0.4, speedY: 0.15 + Math.random() * 0.3, speedZ: (Math.random() - 0.5) * 0.4,
                swaySpeed: 0.5 + Math.random() * 1.5, swayRadius: 0.05 + Math.random() * 0.15, phase: Math.random() * Math.PI * 2
            });
        }
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        sparks = new THREE.Points(geo, new THREE.PointsMaterial({
            size: 0.025, vertexColors: true, transparent: true, opacity: 0.85,
            blending: THREE.AdditiveBlending, depthWrite: false, map: sparkTexture()
        }));
        scene.add(sparks);
    }

    // intro: particles fly in from every side and assemble into the model
    let intro = null;
    const startIntro = (model) => {
        pivot.updateMatrixWorld(true);
        const inv = pivot.matrixWorld.clone().invert();
        const pts = [];
        const mats = [];
        const v = new THREE.Vector3();
        const mtx = new THREE.Matrix4();
        model.traverse((child) => {
            if (!child.isMesh || !child.geometry || !child.geometry.attributes.position) return;
            const pos = child.geometry.attributes.position;
            mtx.multiplyMatrices(inv, child.matrixWorld);
            const step = Math.max(1, Math.floor(pos.count / 7000));
            for (let i = 0; i < pos.count; i += step) {
                v.fromBufferAttribute(pos, i).applyMatrix4(mtx);
                pts.push(v.x, v.y, v.z);
            }
            (Array.isArray(child.material) ? child.material : [child.material]).forEach((m) => {
                m.transparent = true; m.opacity = 0; mats.push(m);
            });
        });
        const n = pts.length / 3;
        if (!n) { mats.forEach((m) => { m.opacity = 1; m.transparent = false; }); return; }
        const targets = new Float32Array(pts);
        const starts = new Float32Array(n * 3);
        const cur = new Float32Array(n * 3);
        const delays = new Float32Array(n);
        const colors = new Float32Array(n * 3);
        for (let i = 0; i < n; i++) {
            const th = Math.random() * Math.PI * 2;
            const ph = Math.acos(2 * Math.random() - 1);
            const r = 5 + Math.random() * 5;
            starts[i * 3] = r * Math.sin(ph) * Math.cos(th);
            starts[i * 3 + 1] = r * Math.cos(ph);
            starts[i * 3 + 2] = r * Math.sin(ph) * Math.sin(th);
            delays[i] = Math.random() * 0.3;
            const w = 0.25 + Math.random() * 0.75;
            colors[i * 3] = accent[0] * (1 - w) + w;
            colors[i * 3 + 1] = accent[1] * (1 - w) + w;
            colors[i * 3 + 2] = accent[2] * (1 - w) + w;
        }
        cur.set(starts);
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(cur, 3));
        geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        const points = new THREE.Points(geo, new THREE.PointsMaterial({
            size: 0.04, vertexColors: true, transparent: true, opacity: 1,
            blending: THREE.AdditiveBlending, depthWrite: false, map: sparkTexture()
        }));
        points.frustumCulled = false;
        pivot.add(points);
        intro = { points, targets, starts, cur, delays, mats, n, t: 0, dur: 2.8 };
    };
    const stepIntro = (dt) => {
        intro.t += dt;
        const k = Math.min(1, intro.t / intro.dur);
        const { starts, targets, cur, delays, n } = intro;
        for (let i = 0; i < n; i++) {
            const p = Math.max(0, Math.min(1, (k - delays[i]) / 0.5));
            const e = 1 - Math.pow(1 - p, 3);
            const a = (1 - e) * 1.2;
            const ca = Math.cos(a), sa = Math.sin(a);
            const j = i * 3;
            const x = starts[j] + (targets[j] - starts[j]) * e;
            const z = starts[j + 2] + (targets[j + 2] - starts[j + 2]) * e;
            cur[j] = x * ca - z * sa;
            cur[j + 1] = starts[j + 1] + (targets[j + 1] - starts[j + 1]) * e;
            cur[j + 2] = x * sa + z * ca;
        }
        intro.points.geometry.attributes.position.needsUpdate = true;
        const fade = Math.max(0, Math.min(1, (k - 0.8) / 0.2));
        intro.mats.forEach((m) => { m.opacity = fade; });
        intro.points.material.opacity = 1 - fade * fade;
        if (k >= 1) {
            intro.mats.forEach((m) => { m.opacity = 1; m.transparent = false; m.needsUpdate = true; });
            pivot.remove(intro.points);
            intro.points.geometry.dispose();
            intro.points.material.dispose();
            intro = null;
        }
    };

    // model
    let pivot = null, mixer = null;
    const baseRot = ((Number(cfg.rotation) || 0) * Math.PI) / 180;
    if (cfg.model) {
        new GLTFLoader().load(cfg.model, (gltf) => {
            if (state.dead) return;
            const model = gltf.scene;
            pivot = new THREE.Group();
            scene.add(pivot);
            pivot.add(model);
            model.traverse((child) => {
                if (!child.isMesh || !child.material) return;
                child.castShadow = true;
                child.receiveShadow = true;
                const mats = Array.isArray(child.material) ? child.material : [child.material];
                mats.forEach((mat) => {
                    if (!cfg.keepColors) {
                        if ('roughness' in mat) mat.roughness = 0.42;
                        if ('metalness' in mat) mat.metalness = 0.92;
                        if (mat.color) { const c = hexToRgb(cfg.modelColor, '7d8388'); mat.color.setRGB(c[0], c[1], c[2], THREE.SRGBColorSpace); }
                        if (mat.map) { mat.map = null; }
                        mat.needsUpdate = true;
                    }
                    mat.flatShading = false;
                    if (mat.map) mat.map.anisotropy = 16;
                });
            });
            if (gltf.animations && gltf.animations.length) {
                mixer = new THREE.AnimationMixer(model);
                gltf.animations.forEach((clip) => mixer.clipAction(clip).play());
            }
            const size = new THREE.Box3().setFromObject(model).getSize(new THREE.Vector3());
            const maxDim = Math.max(size.x, size.y, size.z);
            model.scale.setScalar((3.5 * ((Number(cfg.scale) || 100) / 100)) / (maxDim > 0.0001 ? maxDim : 1));
            model.updateMatrixWorld(true);
            model.position.sub(new THREE.Box3().setFromObject(model).getCenter(new THREE.Vector3()));
            pivot.position.y = -0.4;
            if (cfg.intro !== false) startIntro(model);
            root.classList.add('h3d--model-ready');
        }, undefined, (err) => { console.error('[3D hero] Could not load model:', cfg.model, err); });
    }

    const resize = () => {
        sizes.width = stage.clientWidth || window.innerWidth;
        sizes.height = stage.clientHeight || window.innerHeight;
        camera.aspect = sizes.width / sizes.height;
        camera.updateProjectionMatrix();
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(sizes.width, sizes.height, false);
        const pr = renderer.getPixelRatio();
        uniforms.uResolution.value.set(sizes.width * pr, sizes.height * pr);
    };
    resize();
    const onResize = () => { setTop(); resize(); };
    window.addEventListener('resize', onResize);

    // pointer
    let mouseX = 0, mouseY = 0, tMouseX = 0, tMouseY = 0;
    let cx = sizes.width / 2, cy = sizes.height / 2, ox = cx, oy = cy;
    const inner = root.querySelector('.h3d-cursor-inner');
    const outer = root.querySelector('.h3d-cursor-outer');
    const useCursor = !!cfg.cursor && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (useCursor) stage.classList.add('h3d-has-cursor');
    stage.addEventListener('pointermove', (e) => {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        const r = stage.getBoundingClientRect();
        cx = e.clientX - r.left; cy = e.clientY - r.top;
        tMouseX = (cx / r.width) * 2 - 1;
        tMouseY = (cy / r.height) * 2 - 1;
        if (useCursor) {
            stage.classList.add('h3d-cursor-on');
            if (inner) { inner.style.left = cx + 'px'; inner.style.top = cy + 'px'; }
        }
    });
    stage.addEventListener('pointerleave', () => stage.classList.remove('h3d-cursor-on'));

    let visible = true;
    if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; }, { rootMargin: '100px' }).observe(root);
    }

    const centers = slides.map((_, i) => (N <= 1 ? 0 : (i / (N - 1)) * 0.94));
    const halfW = N <= 1 ? 2 : Math.min(0.07, 0.3 / (N - 1));
    const updateSlides = (scroll) => {
        fills.forEach((f, i) => {
            const p = Math.max(0, Math.min(1, scroll * N - i));
            f.style.height = (p * 100) + '%';
        });
        slides.forEach((s, i) => {
            let on = Math.abs(scroll - centers[i]) <= halfW;
            if (i === 0 && scroll <= centers[0] + halfW * 1.7) on = true;
            if (i === N - 1 && scroll >= centers[i] - halfW * 1.5) on = true;
            s.classList.toggle('is-active', on);
        });
    };
    const updateDots = (scroll) => {
        dots.forEach((dot, i) => {
            const startY = (i * 17) % 80 + 10;
            let speed = 90 + (i * 55) % 180;
            if (i % 2 === 0) speed = -speed;
            let y = startY + scroll * speed;
            y = ((y % 100) + 100) % 100;
            dot.style.top = y + '%';
        });
    };

    const clock = new THREE.Clock();
    let current = 0;
    const lookAt = new THREE.Vector3();
    const frame = () => {
        if (state.dead) return;
        state.raf = requestAnimationFrame(frame);
        const dt = Math.min(clock.getDelta(), 0.1);
        if (!visible) return;
        if (mixer) mixer.update(dt);

        let target = 0;
        const innerMax = root.scrollHeight - root.clientHeight;
        if (innerMax > 2) {
            // phone "fixed block" layout: the hero scrolls inside itself
            target = Math.max(0, Math.min(1, root.scrollTop / innerMax));
        } else {
            const rect = root.getBoundingClientRect();
            const top = parseFloat(root.style.getPropertyValue('--h3d-top')) || 0;
            const max = root.offsetHeight - stage.offsetHeight;
            target = max > 0 ? Math.max(0, Math.min(1, (top - rect.top) / max)) : 0;
        }
        current += (target - current) * 0.025;

        mouseX += (tMouseX - mouseX) * 0.05;
        mouseY += (tMouseY - mouseY) * 0.05;
        ox += (cx - ox) * 0.2; oy += (cy - oy) * 0.2;
        if (useCursor && outer) { outer.style.left = ox + 'px'; outer.style.top = oy + 'px'; }

        if (pivot) {
            pivot.rotation.y = baseRot + mouseX * 0.25;
            pivot.rotation.x = mouseY * 0.15;
        }
        if (intro) stepIntro(dt);

        if (sparks) {
            const p = sparks.geometry.attributes.position.array;
            const time = clock.elapsedTime;
            const vel = Math.abs(target - current);
            const mult = 1.0 + vel * 9.0;
            const turb = vel * 0.8;
            for (let i = 0; i < sparkCount; i++) {
                const k = i * 3, d = sparkData[i];
                p[k] += d.speedX * dt * mult;
                p[k + 1] += d.speedY * dt * mult;
                p[k + 2] += d.speedZ * dt * mult;
                const sway = d.swayRadius * (1.0 + turb * 4.0);
                p[k] += Math.sin(time * d.swaySpeed + d.phase) * sway * dt;
                p[k + 2] += Math.cos(time * d.swaySpeed + d.phase) * sway * dt;
                if (p[k + 1] > 3.0 || Math.abs(p[k]) > 3.5 || Math.abs(p[k + 2]) > 3.5) {
                    p[k + 1] = -2.5;
                    p[k] = (Math.random() - 0.5) * 3.0;
                    p[k + 2] = (Math.random() - 0.5) * 3.0;
                }
            }
            sparks.geometry.attributes.position.needsUpdate = true;
        }

        const portrait = sizes.width <= 820 && sizes.width < sizes.height;
        const phi = current * Math.PI * 2.0;
        const mult = portrait ? Math.max(1, Math.min(3, 1.2 / (sizes.width / sizes.height))) : 1;
        const radius = (4.2 - Math.sin(current * Math.PI) * 0.6) * mult;
        camera.position.set(radius * Math.sin(phi), 0.35 + Math.sin(current * Math.PI) * 0.8, radius * Math.cos(phi));
        const ease = (Math.cos(Math.min(1.0, current / 0.28) * Math.PI) + 1.0) * 0.5;
        lookAt.set(portrait ? 0 : -0.9 * ease, portrait ? -0.15 - 0.67 * mult : -0.15, 0);
        camera.lookAt(lookAt);

        uniforms.uTime.value = clock.elapsedTime;
        uniforms.uMouse.value.set(mouseX, -mouseY);
        uniforms.uScroll.value = current;

        updateSlides(current);
        updateDots(current);
        renderer.render(scene, camera);
    };
    updateSlides(0);
    frame();

    state.destroy = () => {
        state.dead = true;
        cancelAnimationFrame(state.raf);
        window.removeEventListener('resize', onResize);
        renderer.dispose();
    };
}

function destroy(root) {
    const s = instances.get(root);
    if (s && s.destroy) s.destroy();
    instances.delete(root);
}

function boot() { document.querySelectorAll('[data-h3d]').forEach(init); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
document.addEventListener('shopify:section:load', (e) => { e.target.querySelectorAll('[data-h3d]').forEach(init); });
document.addEventListener('shopify:section:unload', (e) => { e.target.querySelectorAll('[data-h3d]').forEach(destroy); });
